'use client';

import { useState, useEffect } from 'react';
import { API_BASE_URL } from '../lib/config';

export default function NumdleGame({ sessionId }) {
  const [gameSessionId, setGameSessionId] = useState(() => `game_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [game, setGame] = useState(null);
  const [currentGuess, setCurrentGuess] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [targetUser, setTargetUser] = useState(null);

  useEffect(() => {
    startGame();
  }, []);

  const startGame = async () => {
    setLoading(true);
    setError(null);
    try {
      const { protectedFetch } = await import('../lib/recaptcha');
      
      const response = await protectedFetch(`${API_BASE_URL}/api/game/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, gameSessionId }),
        action: 'start_game'
      });

      if (response.ok) {
        const data = await response.json();
        setGame(data.game);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to start game');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (key) => {
    if (game?.isComplete) return;

    if (key === 'Enter') {
      if (currentGuess.length === 10) {
        submitGuess();
      }
    } else if (key === 'Backspace') {
      setCurrentGuess(prev => prev.slice(0, -1));
    } else if (/^\d$/.test(key) && currentGuess.length < 10) {
      setCurrentGuess(prev => prev + key);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      handleKeyPress(e.key);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentGuess, game]);

  const submitGuess = async () => {
    if (currentGuess.length !== 10) return;

    try {
      const { protectedFetch } = await import('../lib/recaptcha');
      
      const response = await protectedFetch(`${API_BASE_URL}/api/game/guess`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, gameSessionId, guess: currentGuess }),
        action: 'submit_guess'
      });

      if (response.ok) {
        const data = await response.json();
        setGame(data.game);
        setCurrentGuess('');

        if (data.targetUser) {
          setTargetUser(data.targetUser);
        }

        if (data.game.isComplete) {
          setTimeout(() => setShowModal(true), 2000);
        }
      } else {
        const err = await response.json();
        setError(err.error || 'Invalid guess');
        setTimeout(() => setError(null), 2000);
      }
    } catch (error) {
      setError('Network error');
      setTimeout(() => setError(null), 2000);
    }
  };

  const resetGame = async () => {
    setShowModal(false);
    setCurrentGuess('');
    setGame(null);
    setTargetUser(null);
    const newGameSessionId = `game_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    setGameSessionId(newGameSessionId);
    
    setLoading(true);
    setError(null);
    try {
      const { protectedFetch } = await import('../lib/recaptcha');
      
      const response = await protectedFetch(`${API_BASE_URL}/api/game/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId, gameSessionId: newGameSessionId }),
        action: 'start_game'
      });

      if (response.ok) {
        const data = await response.json();
        setGame(data.game);
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to start game');
      }
    } catch (error) {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem' }}>Loading game...</div>;
  }

  if (error && !game) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem', color: '#dc2626' }}>
        {error}
        <button onClick={startGame} style={{ display: 'block', margin: '1rem auto', padding: '0.5rem 1rem' }}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '500px', margin: '2rem auto' }}>
      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Guess the Phone Number</h2>
        <p style={{ color: '#666', fontSize: '0.9rem' }}>
          {game?.isComplete ? 'Game Over' : `${7 - (game?.attemptsUsed || 0)} attempts remaining`}
        </p>
        {error && (
          <div style={{ color: '#dc2626', fontSize: '0.9rem', marginTop: '0.5rem' }}>
            {error}
          </div>
        )}
      </div>

      <div style={{ marginBottom: '2rem' }}>
        {[...Array(7)].map((_, i) => (
          <GameRow
            key={i}
            guess={game?.guesses[i]}
            currentGuess={i === game?.attemptsUsed ? currentGuess : null}
          />
        ))}
      </div>

      <Keypad onKeyPress={handleKeyPress} disabled={game?.isComplete} />

      {showModal && (
        <GameModal
          isWon={game?.isWon}
          attempts={game?.attemptsUsed}
          targetNumber={game?.targetNumber}
          targetUser={targetUser}
          onReset={resetGame}
        />
      )}
    </div>
  );
}

function GameRow({ guess, currentGuess }) {
  if (guess) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
        {guess.result.map((item, i) => (
          <div
            key={i}
            style={{
              width: '45px',
              height: '45px',
              border: '2px solid',
              borderColor: item.color === 'green' ? '#5ac85a' : item.color === 'yellow' ? '#e2cc68' : '#a1a1a1',
              backgroundColor: item.color === 'green' ? '#5ac85a' : item.color === 'yellow' ? '#e2cc68' : '#a1a1a1',
              color: '#fff',
              margin: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              borderRadius: '4px',
              animation: `flip 0.5s ease forwards ${i * 0.2}s`
            }}
          >
            {item.digit}
          </div>
        ))}
      </div>
    );
  }

  if (currentGuess) {
    const digits = currentGuess.split('');
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
        {[...Array(10)].map((_, i) => (
          <div
            key={i}
            style={{
              width: '45px',
              height: '45px',
              border: '2px solid',
              borderColor: digits[i] ? '#333' : '#ddd',
              margin: '2px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              borderRadius: '4px',
              animation: digits[i] ? 'bounce 0.1s ease-in-out' : 'none'
            }}
          >
            {digits[i] || ''}
          </div>
        ))}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '4px' }}>
      {[...Array(10)].map((_, i) => (
        <div
          key={i}
          style={{
            width: '45px',
            height: '45px',
            border: '2px solid #ddd',
            margin: '2px',
            borderRadius: '4px'
          }}
        />
      ))}
    </div>
  );
}

function Keypad({ onKeyPress, disabled }) {
  const rows = [
    ['1', '2', '3'],
    ['4', '5', '6'],
    ['7', '8', '9'],
    ['Backspace', '0', 'Enter']
  ];

  return (
    <div style={{ maxWidth: '400px', margin: '0 auto' }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'center', marginBottom: '8px' }}>
          {row.map((key) => (
            <button
              key={key}
              onClick={() => onKeyPress(key)}
              disabled={disabled}
              style={{
                width: key === 'Backspace' || key === 'Enter' ? '80px' : '60px',
                height: '50px',
                margin: '4px',
                fontSize: key === 'Backspace' || key === 'Enter' ? '0.8rem' : '1.2rem',
                fontWeight: 'bold',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: disabled ? '#e5e7eb' : '#f3f4f6',
                cursor: disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                if (!disabled) e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'scale(1)';
              }}
            >
              {key === 'Backspace' ? '⌫' : key === 'Enter' ? '↵' : key}
            </button>
          ))}
        </div>
      ))}
    </div>
  );
}

function GameModal({ isWon, attempts, targetNumber, targetUser, onReset }) {
  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      overflow: 'auto'
    }}>
      <div style={{
        backgroundColor: '#fff',
        padding: '2rem',
        borderRadius: '8px',
        maxWidth: '500px',
        maxHeight: '90vh',
        overflow: 'auto',
        textAlign: 'center',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
        margin: '1rem'
      }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: isWon ? '#5ac85a' : '#dc2626' }}>
          {isWon ? 'You Won!' : 'Game Over'}
        </h2>
        <p style={{ marginBottom: '1rem', fontSize: '1.1rem' }}>
          {isWon ? `You guessed it in ${attempts} attempts!` : `Better luck next time!`}
        </p>
        {targetNumber && (
          <p style={{ marginBottom: '1.5rem', fontSize: '0.9rem', color: '#666' }}>
            The number was: <strong>{targetNumber}</strong>
          </p>
        )}

        {isWon && targetUser && (
          <div style={{
            marginTop: '1.5rem',
            padding: '1.5rem',
            backgroundColor: '#f0fdf4',
            borderRadius: '8px',
            textAlign: 'left',
            border: '2px solid #5ac85a'
          }}>
            <h3 style={{ fontSize: '1.2rem', marginBottom: '1rem', color: '#166534', textAlign: 'center' }}>
              🎁 Your Reward: Meet {targetUser.name}!
            </h3>
            
            <div style={{ marginBottom: '0.75rem' }}>
              <strong>📱 Phone:</strong> {targetUser.phone}
            </div>
            
            {targetUser.email && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>📧 Email:</strong> {targetUser.email}
              </div>
            )}
            
            {targetUser.bio && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>💭 Bio:</strong> {targetUser.bio}
              </div>
            )}
            
            {targetUser.location && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>📍 Location:</strong> {targetUser.location}
              </div>
            )}
            
            {targetUser.instagram && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>📷 Instagram:</strong> @{targetUser.instagram}
              </div>
            )}
            
            {targetUser.twitter && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>🐦 Twitter:</strong> {targetUser.twitter}
              </div>
            )}
            
            {targetUser.linkedin && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>💼 LinkedIn:</strong> <a href={targetUser.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>Profile</a>
              </div>
            )}
            
            {targetUser.github && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>💻 GitHub:</strong> {targetUser.github}
              </div>
            )}
            
            {targetUser.website && (
              <div style={{ marginBottom: '0.75rem' }}>
                <strong>🌐 Website:</strong> <a href={targetUser.website} target="_blank" rel="noopener noreferrer" style={{ color: '#3b82f6' }}>{targetUser.website}</a>
              </div>
            )}
            
            {targetUser.interests && targetUser.interests.length > 0 && (
              <div style={{ marginTop: '1rem' }}>
                <strong>✨ Interests:</strong>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginTop: '0.5rem' }}>
                  {targetUser.interests.map((interest, i) => (
                    <span key={i} style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: '#dcfce7',
                      borderRadius: '12px',
                      fontSize: '0.85rem',
                      color: '#166534'
                    }}>
                      {interest}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        <button
          onClick={onReset}
          style={{
            marginTop: '1.5rem',
            padding: '0.75rem 1.5rem',
            fontSize: '1rem',
            fontWeight: 'bold',
            border: 'none',
            borderRadius: '4px',
            backgroundColor: '#3b82f6',
            color: '#fff',
            cursor: 'pointer'
          }}
        >
          {isWon ? 'Start New Game' : 'Try New Number'}
        </button>
      </div>
    </div>
  );
}
